import * as React from 'react';
import { Link } from 'react-router-dom';

import { HelpContactSlot } from '@/demo';
import { PageHeader } from '@/design';
import {
  AuthShell,
  AuthSplash,
  DoorHeading,
  SIGN_IN_REQUEST_ROUTE,
  useAuth
} from '@/domains/auth';
import { PortalBoundary } from '../boundary';
import { PortalShell } from './portal-shell';

/**
 * Help — the one Advisor Portal page reachable signed out (the two-portals design, §3.1 and §10
 * ruling 3), because registration's refusals point here before any account exists. Signed in as an
 * advisor it sits in the portal's shell; signed out, at the front door; staff get the boundary page
 * like at any other portal address.
 *
 * **It offers no contact channel, because Veritant has none yet** (ruled 2026-09-28: it will be an
 * email address). Until the address exists, the page says so — rather than printing a plausible
 * `support@`, which is the one sort of placeholder somebody eventually uses for real (the
 * onboarding study's §4.8). What it can already say is true today: what a reference is for, how
 * saving works, and the way in for someone who cannot sign in yet. With the demo on, a
 * badged mock contact block follows "Reaching Veritant" (`HelpContactSlot`) — still no address.
 */
export function HelpRoute() {
  const { status, user } = useAuth();

  if (status === 'pending') return <AuthSplash />;

  if (status === 'anonymous' || !user) {
    return (
      <AuthShell title="Help" shape="page">
        <DoorHeading title="Help" lede={LEDE} />
        <HelpContent signedIn={false} />
      </AuthShell>
    );
  }

  if (user.kind !== 'advisor') return <PortalBoundary />;

  return (
    <PortalShell>
      <PageHeader title="Help" lede={LEDE} />
      <div className="mx-auto w-full max-w-[1120px] px-6 pt-6 pb-16 lg:px-10">
        <div className="max-w-[42rem]">
          <HelpContent signedIn />
        </div>
      </div>
    </PortalShell>
  );
}

const LEDE = 'What to do when a survey, a number or signing in is not what you expect.';

function HelpContent({ signedIn }: { signedIn: boolean }) {
  return (
    <div className="grid gap-8 text-[0.9375rem] leading-relaxed text-[var(--color-midnight-blue)]">
      <HelpSection title="Reaching Veritant">
        <p>
          Veritant has not published a contact address for this portal yet. When it does, it will be
          on this page.
        </p>
      </HelpSection>

      <HelpContactSlot signedIn={signedIn} />

      <HelpSection title="The reference to quote">
        <p>
          Some screens give you a reference — a registration that cannot continue shows one. Keep
          it. It is how Veritant’s research team finds what happened, and it names nobody, so it is
          safe to send.
        </p>
      </HelpSection>

      <HelpSection title="Your answers are saved as you go">
        <p>
          Every answer is saved while you type, and a survey opens where you stopped. You can
          leave at any time with "Finish later", and correct a submitted survey until its cycle
          closes.
        </p>
      </HelpSection>

      <HelpSection title="Signing in">
        <p>
          You sign in at Invent with the email address of your account. If you have no invitation
          to sign in yet, or yours has expired,{' '}
          <Link
            to={SIGN_IN_REQUEST_ROUTE}
            className="text-[var(--color-sapphire)] underline underline-offset-4"
          >
            ask for one
          </Link>
          .
        </p>
      </HelpSection>
    </div>
  );
}

function HelpSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h2 className="text-lg font-medium text-[var(--color-royal-blue)]">{title}</h2>
      {children}
    </section>
  );
}
